import {Component, DestroyRef, effect, inject, OnInit, untracked, ViewChild, WritableSignal} from '@angular/core';
import {BrandsService} from '../../services/brands.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  BrandOverviewResponse,
  CompetitionFiltersResponse,
  ContentCategoryPayload,
  CREATIVE_GALLERY_SORT_OPTIONS,
  CreativeGalleryPayload,
  CreativeGalleryResponse,
  CreativeGallerySort,
  InventoryQualityTableColumns,
  InventoryQualityTablePayload,
  InventoryQualityTableResponse,
  MyBrandAudienceGenderPayload,
  MyBrandAudienceGenderResponse,
  OverviewPayload,
  PerformanceMapMetrics,
  PerformanceMapPayload,
  PerformanceMapResponse,
  PerformanceMapType,
  GalleryNavigation, 
  GENDER_CHART_COLORS,
  PerformanceOverTimePayload,
  PerformanceComparisonMetric,
  MyBrandPerformanceMetrics,
} from './types';
import {WidgetContainerComponent} from './widget-container/widget-container.component';
import {catchError, finalize, of, Subject, switchMap} from 'rxjs';
import {SitesTableComponent} from './sites-table/sites-table.component';
import {PaginatorComponent} from '../../shared/components/paginator/paginator.component';
import {NoWidgetDataComponent} from './no-widget-data/no-widget-data.component';
import {FormControl} from '@angular/forms';
import {PageEvent} from '@angular/material/paginator';
import {ChartData, WIDGET_COLOR_SCALE} from '../../shared/types/common';
import {PieChartComponent} from '../../shared/components/charts/content-category-breakdown/pie-chart.component';
import {CreativeGalleryComponent} from './creative-gallery/creative-gallery.component';
import {daysDataShowing, ROWS_PER_PAGE} from '../../utils/constants';
import {OverviewTilesComponent} from '../../shared/components/overview-tiles/overview-tiles.component';
import {PosthogService} from '../../services/posthog.service';
import {PosthogEventName, PosthogEventCode} from '../../services/types';
import {PerformanceMapComponent} from '../../shared/components/charts/performance-map/performance-map.component';
import {TabsComponent} from '../../shared/components/tabs/tabs.component';
import {SortClickEvent, SortDirection, SortOptions, defaultSortOptions} from '../../utils/sort-state';
import {NgIf} from '@angular/common';
import {isSortField} from '../my-campaigns-screen/types';
import {DashboardType, TopBarWrapperComponent} from '../top-bar-wrapper/top-bar-wrapper.component';
import {DashboardFilters, DashboardFiltersPayload, DashboardService} from '../../services/dashboard.service';
import {ActivatedRoute} from '@angular/router';
import {SelectItem, SelectMode} from '../../shared/components/select-menu/select-menu.component';
import {TopLevelMetricsComponent} from '../../shared/components/top-level-metrics/top-level-metrics.component';
import {tooltips} from '../../utils/tooltips';
import {DataTestIds} from '../../../../e2e/types';
import {BrandsFiltersResolverService} from '../../services/brands-filters-resolver.service';
import { MyCompetitionPerformanceWidgetResponse } from '../my-competition-screen/types';
import { PerformanceOverTimeComponent } from '../../shared/components/charts/performance-over-time/performance-over-time.component';
import { Format } from '../../services/util.service';
import {WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN} from '../copilot/types';

@Component({
  selector: 'app-my-brand-screen',
  imports: [
    PieChartComponent,
    WidgetContainerComponent,
    SitesTableComponent,
    PaginatorComponent,
    NgIf,
    CreativeGalleryComponent,
    OverviewTilesComponent,
    PerformanceMapComponent,
    TabsComponent,
    TopBarWrapperComponent,
    NoWidgetDataComponent,
    TopLevelMetricsComponent,
    PerformanceOverTimeComponent,
  ],
  templateUrl: './my-brand-screen.component.html',
  styleUrl: './my-brand-screen.component.scss'
})
export class MyBrandScreenComponent implements OnInit {
  public loadingState = {
    overview: true,
    performanceMap: true,
    contentCategory: true,
    inventoryQuality: true,
    creativeGallery: true,
    performanceOverTime: true,
    audienceGender: true,
  };
  public contentCategoryBreakDown: ChartData[] = [];
  public inventoryQualityTableData: InventoryQualityTableResponse = {sites: [], total: 0};
  public creativeGalleryData: CreativeGalleryResponse = {creativesData: [], total: 0};
  public inventoryQualityCurrentPage: number = 0;
  public initialPage: number = 1;
  public inventoryQualityPageSize: number = 5;
  public creativeGalleryCurrentPage: number = 0;
  public CREATIVE_GALLERY_PAGE_SIZE: number = 8;
  public contentCategoryColors: string[] = WIDGET_COLOR_SCALE;
  public gallerySortOptions: SelectItem<CreativeGallerySort>[] = CREATIVE_GALLERY_SORT_OPTIONS;
  public gallerySort: FormControl<CreativeGallerySort> = new FormControl();
  public overview?: BrandOverviewResponse;
  private pendingGalleryModalNav?: GalleryNavigation;

  public audienceGenderData: MyBrandAudienceGenderResponse | undefined;
  public readonly audienceGenderColors = [GENDER_CHART_COLORS.men, GENDER_CHART_COLORS.women];
  public audienceGenderChartData: ChartData[] = [];

  public performanceOverTimeData: MyCompetitionPerformanceWidgetResponse | undefined;
  public performanceOverTimeMetricOptions: SelectItem[] = MyBrandPerformanceMetrics;
  public performanceOverTimeSelectedOption: SelectItem = MyBrandPerformanceMetrics[0];
  public performanceOverTimeMetricControl: FormControl<PerformanceComparisonMetric | null> = new FormControl(this.performanceOverTimeSelectedOption.key as PerformanceComparisonMetric);
  public performanceOverTimeFormat: Format = 'shortCurrencyNumber';

  public performanceMapData: PerformanceMapResponse = {performance: []};
  public performanceMapMetricOptions: SelectItem[] = PerformanceMapMetrics;
  public performanceMapSelectedOption: SelectItem = PerformanceMapMetrics[0];
  public performanceMapMetricControl: FormControl<string | null> = new FormControl(this.performanceMapSelectedOption.key);
  public performanceMapType: PerformanceMapType = PerformanceMapType.WORLDWIDE;

  protected readonly tooltips = tooltips.myBrand;
  protected readonly daysDataShowing = daysDataShowing;
  protected readonly rowsPerPage = ROWS_PER_PAGE;
  protected readonly SelectMode = SelectMode;

  private inventoryQualitySortOptions: SortOptions = {
    sortField: InventoryQualityTableColumns.BRAND_IMPRESSIONS,
    isAsc: false
  };

  private audienceGenderData$ = new Subject<MyBrandAudienceGenderPayload>();
  private overviewData$ = new Subject<OverviewPayload>();
  private performanceMapData$ = new Subject<PerformanceMapPayload>();
  private contentCategoryData$ = new Subject<ContentCategoryPayload>();
  private inventoryQualityData$ = new Subject<InventoryQualityTablePayload>();
  private creativeGalleryData$ = new Subject<CreativeGalleryPayload>();
  private performanceOverTimeData$ = new Subject<PerformanceOverTimePayload>();

  private filters: CompetitionFiltersResponse = {brands: {}}
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private posthogService = inject(PosthogService);
  private dashboardService = inject(DashboardService);
  private myBrandService = inject(BrandsService);
  private filtersResolverService = inject(BrandsFiltersResolverService);

  protected readonly dashboardFilters: WritableSignal<DashboardFilters> = this.dashboardService.brandsFilters;
  protected brandColorsMap = this.dashboardService.brandColorsMap;
  protected readonly DashboardType = DashboardType;

  protected performanceMapVolumeThreshold: number | null = null;
  protected creativeGalleryVolumeThreshold: number | null = null;
  protected readonly DataTestIds = DataTestIds;

  @ViewChild('inventoryQualityPaginator') inventoryQualityPaginator!: PaginatorComponent;
  @ViewChild('creativeGalleryPaginator') creativeGalleryPaginator!: PaginatorComponent;
  @ViewChild(CreativeGalleryComponent) creativeGalleryCmp!: CreativeGalleryComponent;

  constructor() {
    effect(() => {
      const {brandsOptions, brandColorsMap, brandColorsByNameMap} = this.dashboardService;
      untracked(brandsOptions);
      untracked(brandColorsMap);
      untracked(brandColorsByNameMap);
      this.updateFilters();
      const dashboardFilters = this.dashboardService.brandsFilters();
      this.initializeData(dashboardFilters);
    });
  }

  ngOnInit() {
    try {
      this.filters = this.route.snapshot.data['filters']['brands'] ?? {brands: {}};
    } catch (error) {
    }
    this.initSubjects();
  }

  private updateFilters(): void {
    this.filtersResolverService.resolve().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(filters => {
      this.filters = filters.brands;
    })
  }

  private initSubjects() {
    this.initOverviewSubject();
    this.initPerformanceMapSubject();
    this.initContentCategorySubject();
    this.initInventoryQualitySubject();
    this.initCreativeGallerySubject();
    this.initPerformanceOverTimeSubject();
    this.initAudienceGenderSubject();
  }

  private initOverviewSubject(): void {
    this.overviewData$
      .pipe(
        switchMap((payload: OverviewPayload) => {
          this.loadingState.overview = true;
          return this.myBrandService.getBrandOverview(payload).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loadingState.overview = false;
            }),
            catchError(
              () => {
                this.overview = undefined;
                return of(undefined)
              }
            )
          );
        })
      )
      .subscribe(result => {
        this.overview = result;
      });
  }

  private initPerformanceMapSubject(): void {
    this.performanceMapData$
      .pipe(
        switchMap((payload: PerformanceMapPayload) => {
          this.loadingState.performanceMap = true;
          const performanceMapObservable = this.performanceMapType === PerformanceMapType.US
            ? this.myBrandService.getPerformanceMapUsa(payload)
            : this.myBrandService.getPerformanceMapWorldwide(payload);
          return performanceMapObservable.pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loadingState.performanceMap = false;
            }),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) this.performanceMapData = result;
      });
  }

  private initContentCategorySubject(): void {
    this.contentCategoryData$
      .pipe(
        switchMap((payload: ContentCategoryPayload) => {
          this.loadingState.contentCategory = true;
          return this.myBrandService.getContentCategory(payload).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loadingState.contentCategory = false;
            }),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) this.contentCategoryBreakDown = result.categories;
      });
  }

  private initInventoryQualitySubject(): void {
    this.inventoryQualityData$
      .pipe(
        switchMap((payload) => {
          this.loadingState.inventoryQuality = true;
          return this.myBrandService.getMyBrandInventoryQuality(payload).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loadingState.inventoryQuality = false;
            }),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) this.inventoryQualityTableData = result;
      });
  }

  private initCreativeGallerySubject(): void {
    this.creativeGalleryData$
      .pipe(
        switchMap((payload) => {
          takeUntilDestroyed(this.destroyRef),
          this.loadingState.creativeGallery = true;
          return this.myBrandService.getMyCreativeGallery(payload).pipe(
            finalize(() => {
              this.loadingState.creativeGallery = false;
            }),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) this.creativeGalleryData = result;
        if (this.pendingGalleryModalNav && this.creativeGalleryData.creativesData.length) {
          const openFirst = this.pendingGalleryModalNav === GalleryNavigation.NEXT;
          const target = openFirst ? this.creativeGalleryData.creativesData[0] : this.creativeGalleryData.creativesData[this.creativeGalleryData.creativesData.length - 1];
          this.pendingGalleryModalNav = undefined;
          setTimeout(() => this.creativeGalleryCmp.openCardModal(target.id, 'navigate'), 0);
        }
      });
  }

  private initPerformanceOverTimeSubject(): void {
    this.performanceOverTimeData$
      .pipe(
        switchMap((payload: PerformanceOverTimePayload) => {
          this.loadingState.performanceOverTime = true;
          return this.myBrandService.getPerformanceOverTime(payload).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loadingState.performanceOverTime = false;
            }),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) {
          this.performanceOverTimeData = {
            myBrand: {
              brandKey: this.getMyBrandKey(),
              brandName: this.dashboardFilters().myBrand.label,
              dailyPerformance: result.performance
            },
            myCompetitors: [],
            isUnderDateThreshold: result.isUnderDateThreshold
          };
          this.performanceOverTimeFormat = this.getPerformanceOverTimeFormat();
        }
      });
  }

  public compareOptions(option1: CreativeGallerySort, option2: CreativeGallerySort): boolean {
    return option1 && option2 && option1.sortField === option2.sortField && option1.isAsc === option2.isAsc;
  }

  public onInventoryQualityRowsChange(rowsPerPage: number | null): void {
    if (!rowsPerPage) return;
    this.inventoryQualityPageSize = rowsPerPage;
    this.inventoryQualityPaginator.goToPage(1);
    this.onInventoryQualityPageChange({
      pageIndex: 0,
      pageSize: this.inventoryQualityPageSize,
      length: this.inventoryQualityTableData.total,
      previousPageIndex: this.inventoryQualityCurrentPage,
      ...(this.inventoryQualitySortOptions)
    }, false);
  }

  public onSortFieldChange(): void {
    this.creativeGalleryPaginator.goToPage(1);
    this.onCreativeGalleryPageChange({
      pageIndex: 0,
      pageSize: this.CREATIVE_GALLERY_PAGE_SIZE,
      length: this.creativeGalleryData.total,
      previousPageIndex: this.creativeGalleryCurrentPage
    });
  }

  public onInventoryQualityPageChange(event: PageEvent, toTrack = true): void {
    this.inventoryQualityCurrentPage = event.pageIndex as number;
    this.getFilteredInventoryQualityData({
      brands: [this.getMyBrandKey()],
      categories: [], // TODO: remove categories after server support
      limit: this.inventoryQualityPageSize,
      offset: this.inventoryQualityCurrentPage * this.inventoryQualityPageSize,
      ...(this.inventoryQualitySortOptions),
      ...(this.dashboardService.getDashboardFiltersPayload())
    });
    if (toTrack) {
      this.posthogService.capture(PosthogEventName.INVENTORY_QUALITY_TABLE_PAGINATION, {
        pageNumber: event.pageIndex + 1,
        lastPageNumber: Math.ceil(event.length / event.pageSize),
        [PosthogEventCode.WIDGET_NAME]: 'inventory_quality_table'
      });
    }
  }

  public onCreativeGalleryPageChange(event: PageEvent) {
    this.creativeGalleryCurrentPage = event.pageIndex as number;
    this.getFilteredCreativeGallery(event.pageIndex);
  }

  public onCreativeModalEdgeNav(direction: GalleryNavigation) {
    const total = this.creativeGalleryData.total;
    const pageSize = this.CREATIVE_GALLERY_PAGE_SIZE;
    const lastPageIndex = Math.max(0, Math.ceil(total / pageSize) - 1);
    const targetIndex = direction === 'next' ? this.creativeGalleryCurrentPage + 1 : this.creativeGalleryCurrentPage - 1;
    if (targetIndex < 0) {
      this.pendingGalleryModalNav = direction;
      this.creativeGalleryPaginator?.goToPage(lastPageIndex + 1);
      return;
    }
    if(targetIndex > lastPageIndex) {
      this.pendingGalleryModalNav = direction;
      this.creativeGalleryPaginator?.goToPage(1);
      return;
    }
    this.pendingGalleryModalNav = direction;
    this.creativeGalleryPaginator?.goToPage(targetIndex + 1);
  }

  public onPerformanceMapMetricChange() {
    this.performanceMapSelectedOption = this.performanceMapMetricOptions.find(option => option.key === this.performanceMapMetricControl.value) || PerformanceMapMetrics[0];
    this.posthogService.capture(PosthogEventName.METRIC_SELECTED, { [PosthogEventCode.METRIC_NAME]: this.performanceMapMetricControl.value, [PosthogEventCode.WIDGET_NAME]: 'performance_map' });
  }

  public onPerformanceOverTimeMetricChange() {
    this.performanceOverTimeSelectedOption = this.performanceOverTimeMetricOptions.find(option => option.key === this.performanceOverTimeMetricControl.value) || MyBrandPerformanceMetrics[0];
    this.posthogService.capture(PosthogEventName.METRIC_SELECTED, { [PosthogEventCode.METRIC_NAME]: this.performanceOverTimeMetricControl.value, [PosthogEventCode.WIDGET_NAME]: 'performance_over_time' });

    const filtersPayload = this.dashboardService.getDashboardFiltersPayload();
    const brandKey = this.getMyBrandKey();
    this.performanceOverTimeData$.next(this.getPerformanceOverTimePayload(brandKey, filtersPayload));
    this.performanceOverTimeFormat = this.getPerformanceOverTimeFormat();
  }

  protected onPerformanceMapVolumeFilter(threshold: number | null): void {
    this.performanceMapVolumeThreshold = threshold;
    const filtersPayload = this.dashboardService.getDashboardFiltersPayload();
    const brandKey = this.getMyBrandKey();
    this.performanceMapData$.next(this.getPerformanceMapPayload([brandKey], filtersPayload));
    this.posthogService.captureVolumeFilter('performance_map', threshold);
  }

  protected onCreativeGalleryVolumeFilter(threshold: number | null): void {
    this.creativeGalleryVolumeThreshold = threshold;
    const filtersPayload = this.dashboardService.getDashboardFiltersPayload();
    const brandKey = this.getMyBrandKey();
    this.creativeGalleryData$.next(this.getMyCreativeGalleryPayload([brandKey], filtersPayload, this.gallerySort.value));
    this.creativeGalleryPaginator?.goToPage(1);
    this.posthogService.captureVolumeFilter('creative_gallery', threshold);
  }

  public switchPerformanceMapTab(tab: string): void {
    this.loadingState.performanceMap = true;
    const isUs = tab.toLowerCase() === PerformanceMapType.US;

    const filtersPayload = this.dashboardService.getDashboardFiltersPayload();
    const brandKey = this.getMyBrandKey();
    const payload = this.getPerformanceMapPayload([brandKey], filtersPayload);
    this.performanceMapType = isUs ? PerformanceMapType.US : PerformanceMapType.WORLDWIDE;
    this.posthogService.capture(PosthogEventName.MAP_REGION_SELECTED, { [PosthogEventCode.MAR_REGION]: this.performanceMapType, [PosthogEventCode.WIDGET_NAME]: 'performance_map' });
    this.performanceMapData$.next(payload);
  }

  private initializeData(dashboardFilters: DashboardFilters): void {
    const filtersPayload = this.dashboardService.getDashboardFiltersPayload(dashboardFilters);

    const creativeGalleryFilterKey = this.gallerySortOptions[0].key;
    this.gallerySort.setValue(creativeGalleryFilterKey);

    const brandKey = this.getMyBrandKey();
    this.overviewData$.next(this.getBrandOverviewPayload([brandKey], filtersPayload));
    this.performanceMapData$.next(this.getPerformanceMapPayload([brandKey], filtersPayload));
    this.contentCategoryData$.next(this.getContentCategoryPayload([brandKey], filtersPayload));
    this.inventoryQualityData$.next(this.getInventoryQualityPayload([brandKey], filtersPayload));
    this.creativeGalleryData$.next(this.getMyCreativeGalleryPayload([brandKey], filtersPayload, creativeGalleryFilterKey));
    this.performanceOverTimeData$.next(this.getPerformanceOverTimePayload(brandKey, filtersPayload));
    this.audienceGenderData$.next({ brandsKeys: [brandKey], ...filtersPayload });

    this.inventoryQualityPaginator?.goToPage(1);
    this.creativeGalleryPaginator?.goToPage(1);
    this.initialPage = 1;
  }

  private getBrandOverviewPayload(brandsKeys: string[], filters: DashboardFiltersPayload): OverviewPayload {
    return { brandsKeys: brandsKeys, ...filters };
  }

  private getPerformanceMapPayload(brandsKeys: string[], filters: DashboardFiltersPayload): PerformanceMapPayload {
    return { brandsKeys: brandsKeys, ...filters, ...(this.performanceMapVolumeThreshold ? { minImpressions: this.performanceMapVolumeThreshold } : {}) };
  }

  private getContentCategoryPayload(brandsKeys: string[], filters: DashboardFiltersPayload): ContentCategoryPayload {
    return { brands: brandsKeys, ...filters };
  }

  private getInventoryQualityPayload(brandsKeys: string[], filters: DashboardFiltersPayload): InventoryQualityTablePayload {
    return {
      brands: brandsKeys,
      categories: [],
      ...(this.inventoryQualitySortOptions),
      limit: this.inventoryQualityPageSize,
      offset: 0,
      ...filters
    }
  }

  private getMyCreativeGalleryPayload(brandsKeys: string[], filters: DashboardFiltersPayload, creativeGalleryFilterKey: CreativeGallerySort): CreativeGalleryPayload {
    return {
      brandsKeys: brandsKeys,
      sortField: creativeGalleryFilterKey.sortField,
      isAsc: creativeGalleryFilterKey.isAsc,
      limit: this.CREATIVE_GALLERY_PAGE_SIZE,
      offset: 0,
      ...filters,
      ...(this.creativeGalleryVolumeThreshold ? { minImpressions: this.creativeGalleryVolumeThreshold } : {})
    }
  }

  private initAudienceGenderSubject(): void {
    this.audienceGenderData$
      .pipe(
        switchMap((payload: MyBrandAudienceGenderPayload) => {
          this.loadingState.audienceGender = true;
          return this.myBrandService.getMyBrandAudienceGender(payload).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => { this.loadingState.audienceGender = false; }),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        this.audienceGenderData = result ?? undefined;
        this.audienceGenderChartData = this.audienceGenderData
          ? [
              { name: 'Men', share: this.audienceGenderData.menPercentage ?? undefined },
              { name: 'Women', share: this.audienceGenderData.womenPercentage ?? undefined },
            ]
          : [];
      });
  }

  private getPerformanceOverTimePayload(brandKey: string, filters: DashboardFiltersPayload): PerformanceOverTimePayload {
    return {
      brandKey,
      metric: this.performanceOverTimeMetricControl.value as PerformanceComparisonMetric,
      ...filters
    };
  }

  private getFilteredInventoryQualityData(filters: InventoryQualityTablePayload): void {
    this.inventoryQualityData$.next(filters);
  }

  private getFilteredCreativeGallery(pageIndex: number) {
    this.creativeGalleryData$.next({
      brandsKeys: [this.getMyBrandKey()],
      limit: this.CREATIVE_GALLERY_PAGE_SIZE,
      offset: pageIndex * this.CREATIVE_GALLERY_PAGE_SIZE,
      ...this.dashboardService.getDashboardFiltersPayload(),
      ...this.gallerySort.value,
      ...(this.creativeGalleryVolumeThreshold ? { minImpressions: this.creativeGalleryVolumeThreshold } : {})
    });
  }

  protected onSortInventoryQualityTable(event: SortClickEvent): void {
    if ((event.direction !== SortDirection.default) && isSortField(event.field)) {
      this.inventoryQualitySortOptions = {
        sortField: event.field,
        isAsc: event.direction === SortDirection.asc,
      }
    } else {
      this.inventoryQualitySortOptions = defaultSortOptions;
    }

    this.inventoryQualityPaginator?.goToPage(1);
    this.onInventoryQualityPageChange({
      pageIndex: 0,
      pageSize: this.inventoryQualityPageSize,
      length: this.inventoryQualityTableData.total,
      previousPageIndex: this.inventoryQualityCurrentPage,
      ...(this.inventoryQualitySortOptions)
    }, false);
  }

  protected getMyBrandKey(): string {
    return this.dashboardFilters().myBrand.key;
  }

  private getPerformanceOverTimeFormat(): Format {
    switch (this.performanceOverTimeMetricControl.value) {
      case PerformanceComparisonMetric.IMPRESSIONS:
        return 'shortNumberWithFloor';
      case PerformanceComparisonMetric.DWELL_TIME:
        return 'seconds';
      case PerformanceComparisonMetric.CPM:
        return 'currency';
      case PerformanceComparisonMetric.SPEND:
        return 'shortCurrencyNumber';
      case PerformanceComparisonMetric.ATTENTION:
        return 'shortPercentage';
        default:
          return 'shortNumber'
    }
  }

  protected readonly performanceMetricOptions = MyBrandPerformanceMetrics;
  protected readonly WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN = WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN;
}
